import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";

const PROJECT_CATEGORIES = [
    { value: 'WORK',      label: 'Work' },
    { value: 'PERSONAL',  label: 'Personal' },
    { value: 'EDUCATION', label: 'Education' },
    { value: 'LEARNING', label: 'Learning' },
    { value: 'HEALTH',    label: 'Health' },
    { value: 'FINANCE',   label: 'Finance' },
    { value: 'SIDE PROJECT',   label: 'Side project' },
    { value: 'OTHER',     label: 'Other' },
];

const PRIORITIES = [
    { value: 1, label: "🔴  High" },
    { value: 2, label: "🟡  Medium" },
    { value: 3, label: "🟢  Low" },
];

const inputClass =
    "bg-[#2A2B32] text-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full";
const labelClass = "text-gray-400 text-xs font-medium uppercase tracking-wide mb-1";

export default function AddProjectModal({ onClose, onSubmit }) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        goal: "",
        category: "",
        priority: "",
        start_date: "",
        end_date: "",
    });

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const set = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = () => {
        const payload = {
            name: form.name || undefined,
            description: form.description || undefined,
            goal: form.goal || undefined,
            category: form.category || undefined,
            priority: form.priority ? Number(form.priority) : undefined,
            start_date: form.start_date ? new Date(form.start_date).getTime() : undefined,
            end_date: form.end_date ? new Date(form.end_date).getTime() : undefined,
        };
        // Remove undefined keys
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        onSubmit(payload);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 px-4" onClick={handleBackdropClick}>
            <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl shadow-xl p-6 w-full max-w-lg relative">

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-200"
                >
                    <X size={20} />
                </button>

                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-100 mb-5">New Project</h2>

                <div className="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-1">

                    {/* Name */}
                    <div className="flex flex-col">
                        <label htmlFor="proj-name" className={labelClass}>Project Title <span className="text-indigo-400">*</span></label>
                        <input
                            id="proj-name"
                            type="text"
                            value={form.name}
                            onChange={set("name")}
                            className={inputClass}
                            placeholder="e.g. Launch new website"
                        />
                    </div>

                    {/* Description */}
                    <div className="flex flex-col">
                        <label htmlFor="proj-desc" className={labelClass}>Description</label>
                        <textarea
                            id="proj-desc"
                            value={form.description}
                            onChange={set("description")}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="Brief overview of the project…"
                        />
                    </div>

                    {/* Goal */}
                    <div className="flex flex-col">
                        <label htmlFor="proj-goal" className={labelClass}>Goal</label>
                        <textarea
                            id="proj-goal"
                            value={form.goal}
                            onChange={set("goal")}
                            rows={2}
                            className={`${inputClass} resize-none`}
                            placeholder="What does success look like?"
                        />
                    </div>

                    {/* Category & Priority */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col">
                            <label htmlFor="proj-category" className={labelClass}>Category</label>
                            <select
                                id="proj-category"
                                value={form.category}
                                onChange={set("category")}
                                className={`${inputClass} appearance-none`}
                            >
                                <option value="">Choose a category</option>
                                {PROJECT_CATEGORIES.map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col">
                            <label htmlFor="proj-priority" className={labelClass}>Priority</label>
                            <select
                                id="proj-priority"
                                value={form.priority}
                                onChange={set("priority")}
                                className={`${inputClass} appearance-none`}
                            >
                                <option value="" >Choose priority</option>
                                {PRIORITIES.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Start Date & End Date */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col">
                            <label htmlFor="proj-start" className={labelClass}>Start Date</label>
                            <input
                                id="proj-start"
                                type="date"
                                value={form.start_date}
                                onChange={set("start_date")}
                                className={`${inputClass} [color-scheme:dark]`}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label htmlFor="proj-end" className={labelClass}>End Date</label>
                            <input
                                id="proj-end"
                                type="date"
                                value={form.end_date}
                                onChange={set("end_date")}
                                className={`${inputClass} [color-scheme:dark]`}
                            />
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-[#2A2B32] transition"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSubmit}
                        disabled={!form.name.trim()}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}

AddProjectModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};
